import { inject, Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { firstValueFrom, Subscription } from 'rxjs';
import { NavController } from '@ionic/angular/standalone';

import { RegisterPushTokenRequest } from '@/app/shared/models';
import { environment } from '@/environments/environment';
import { PushTokensApi } from '../api/push-tokens.api';
import { authService } from '../auth/auth.service';
import { storageService } from './storage.service';

const LAST_REGISTRATION_KEY = 'bm_push_registration';

interface LastPushRegistration {
  userKey: string;
  deviceId: string;
  pushToken: string;
  appVersion: string | null;
}

@Injectable({ providedIn: 'root' })
export class PushRegistrationService {
  private readonly pushTokensApi = inject(PushTokensApi);
  private readonly nav = inject(NavController);
  private readonly zone = inject(NgZone);
  private initialized = false;
  private currentToken: string | null = null;
  private registrationQueue: Promise<void> = Promise.resolve();
  private authSubscription: Subscription | null = null;
  private tokenListener: PluginListenerHandle | null = null;
  private notificationActionListener: PluginListenerHandle | null = null;

  initialize(): void {
    if (this.initialized || !this.isSupportedNativePlatform()) return;
    this.initialized = true;
    void this.setUpListeners();
  }

  destroy(): void {
    this.authSubscription?.unsubscribe();
    this.authSubscription = null;
    if (this.tokenListener) {
      void this.tokenListener.remove();
      this.tokenListener = null;
    }
    if (this.notificationActionListener) {
      void this.notificationActionListener.remove();
      this.notificationActionListener = null;
    }
    this.initialized = false;
  }

  private async setUpListeners(): Promise<void> {
    try {
      this.tokenListener = await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
        const normalizedToken = token.trim();
        if (!normalizedToken) return;

        this.currentToken = normalizedToken;
        if (authService.isAuthenticated()) {
          this.enqueueRegistration(normalizedToken);
        }
      });
    } catch (error) {
      this.logDevelopmentError('Unable to listen for FCM token refreshes.', error);
    }

    try {
      this.notificationActionListener = await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        ({ notification }) => {
          this.handleNotificationAction(notification.data);
        },
      );
    } catch (error) {
      this.logDevelopmentError('Unable to listen for notification navigation.', error);
    }

    this.authSubscription = authService.authState$.subscribe(authenticated => {
      if (authenticated) {
        this.enqueueRegistration();
      } else {
        this.currentToken = null;
        storageService.removeItem(LAST_REGISTRATION_KEY);
      }
    });
  }

  private enqueueRegistration(token?: string): void {
    this.registrationQueue = this.registrationQueue
      .then(() => this.registerIfNeeded(token))
      .catch(error => {
        this.logDevelopmentError('FCM token registration failed.', error);
      });
  }

  private async registerIfNeeded(tokenOverride?: string): Promise<void> {
    if (!authService.isAuthenticated() || !this.isSupportedNativePlatform()) return;

    const { isSupported } = await FirebaseMessaging.isSupported();
    if (!isSupported || !await this.ensureNotificationPermission()) return;

    const pushToken = tokenOverride?.trim()
      || this.currentToken
      || (await FirebaseMessaging.getToken()).token.trim();
    if (!pushToken) return;

    this.currentToken = pushToken;
    const userKey = authService.getAuthSubject();
    if (!userKey) {
      this.logDevelopmentError('Skipped push registration because the authenticated user identity is unavailable.');
      return;
    }

    const [{ identifier: deviceId }, deviceInfo, appInfo] = await Promise.all([
      Device.getId(),
      Device.getInfo(),
      App.getInfo(),
    ]);
    const normalizedDeviceId = deviceId.trim();
    if (!normalizedDeviceId || (deviceInfo.platform !== 'ios' && deviceInfo.platform !== 'android')) return;

    const appVersion = appInfo.version?.trim() || null;
    const registration: LastPushRegistration = {
      userKey,
      deviceId: normalizedDeviceId,
      pushToken,
      appVersion,
    };
    if (this.isCurrentRegistration(registration)) return;

    // The user or token may have changed while native metadata was loading.
    if (!authService.isAuthenticated()
      || authService.getAuthSubject() !== userKey
      || this.currentToken !== pushToken) {
      return;
    }

    const request: RegisterPushTokenRequest = {
      deviceId: normalizedDeviceId,
      platform: deviceInfo.platform,
      pushProvider: 'fcm',
      pushToken,
      appVersion,
      deviceModel: deviceInfo.model?.trim() || null,
    };

    await firstValueFrom(this.pushTokensApi.registerPushToken(request));

    // Cache only a successful registration that still belongs to this session.
    if (authService.isAuthenticated()
      && authService.getAuthSubject() === userKey
      && this.currentToken === pushToken) {
      storageService.setItem(LAST_REGISTRATION_KEY, JSON.stringify(registration));
    }
  }

  private async ensureNotificationPermission(): Promise<boolean> {
    let { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
      ({ receive } = await FirebaseMessaging.requestPermissions());
    }
    return receive === 'granted';
  }

  private isCurrentRegistration(registration: LastPushRegistration): boolean {
    const raw = storageService.getItem(LAST_REGISTRATION_KEY);
    if (!raw) return false;

    try {
      const cached = JSON.parse(raw) as Partial<LastPushRegistration>;
      return cached.userKey === registration.userKey
        && cached.deviceId === registration.deviceId
        && cached.pushToken === registration.pushToken
        && cached.appVersion === registration.appVersion;
    } catch {
      return false;
    }
  }

  private isSupportedNativePlatform(): boolean {
    const platform = Capacitor.getPlatform();
    return Capacitor.isNativePlatform() && (platform === 'ios' || platform === 'android');
  }

  private handleNotificationAction(rawData: unknown): void {
    if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) return;

    const data = rawData as Record<string, unknown>;
    const notificationType = this.readString(data['type']);
    const placeId = this.readPositiveInteger(data['placeId']);
    const reviewId = this.readPositiveInteger(data['matchaReviewId']);
    const opensComments = notificationType === 'ReviewCommented' || notificationType === 'CommentReplied';
    const targetsReview = opensComments || notificationType === 'ReviewLiked';

    if (!placeId || !targetsReview || !reviewId) return;

    this.zone.run(() => {
      void this.openNotificationTarget(placeId, reviewId, opensComments);
    });
  }

  private async openNotificationTarget(placeId: number, reviewId: number, opensComments: boolean): Promise<void> {
    await this.nav.navigateRoot('/tabs/explore', { animated: false });
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await this.nav.navigateForward(['/tabs/notifications/place', placeId], {
      animated: true,
      queryParams: {
        returnUrl: '/tabs/explore',
        reviewId,
        ...(opensComments ? { comments: true } : {}),
      },
    });
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private readPositiveInteger(value: unknown): number | null {
    const parsed = typeof value === 'number' ? value : Number(this.readString(value));
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private logDevelopmentError(message: string, error?: unknown): void {
    if (!environment.production) {
      console.error(`[PushRegistrationService] ${message}`, error ?? '');
    }
  }
}
